import { Elements } from "@stripe/react-stripe-js";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import LoadingPlaceholder from "../../../Components/LoadingPlaceholder";
import {
  ENABLE_CONSUMPTION_BILLING,
  DEFAULT_AUTO_RECHARGE,
} from "../../../Constants/billing";
import { API_Credits } from "../../../API/API_Consumption/credits";
import { isLargeScreen } from "../../MainViewContainer";
import BillingAddress from "./BillingAddress";
import BillingCredits from "./BillingCredits";
import BillingCheckoutForm from "./BillingCheckoutForm";
import BillingHistory from "./BillingHistory";
import BillingPaymentMethod from "./BillingPaymentMethod";
import BillingProgressBar from "./BillingProgressBar";

const BillingDetailsView = ({
  stripeCustomerAPIInProgress,
  stripePortalAPIInProgress,
  paymentMethodAPIInProgress,
  stripeClientBillingHistoryAPIInProgress,
  stripeClientSecretAPIInProgress,
  clientPaymentIntentAPIInProgress,
  paymentMethodsAPIInProgress,
  stripeCustomerData,
  stripeClientSecretData,
  stripeClientBillingHistoryData,
  paymentMethodsData,
  stripePromise,
  isWizard,
  saveCustomerDetails,
  saveInProgress,
  goToPortal,
  isPaymentMethodEditMode,
  setPaymentMethodEditMode,
  handlePaymentPortalOpening,
  handlePaymentMethodSave,
  onPaymentMethodSave,
  setPrimaryPaymentMethod,
  removePaymentMethod,
  loadMoreBillingHistory,
  billingHistory,
  credits,
  onRefresh,
  onCreditsSaved,
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const paymentMethodsRaw = paymentMethodsData?.paymentMethods?.data || [];
  const paymentMethod =
    paymentMethodsRaw.length > 0 ? paymentMethodsRaw[0].card : null;
  const defaultPaymentMethodId =
    stripeCustomerData?.invoice_settings?.default_payment_method;
  // Billing profile flags (recommended, not required): auto-recharge enabled
  // and a monthly spend cap set. Parsed from the Stripe customer metadata.
  const parsedAutoRecharge =
    API_Credits.parseRechargeConfig(stripeCustomerData).autoRecharge ??
    DEFAULT_AUTO_RECHARGE;
  const parsedSpendCap = API_Credits.parseSpendCap(stripeCustomerData)
    .monthlyMaxUsd;
  const hasAutoRecharge = !!parsedAutoRecharge?.enabled;
  const hasSpendCap = parsedSpendCap && parsedSpendCap > 0;

  // Shared recharge-sheet state so the progress bar's "Add credits to start"
  // can open the same sheet as the Recharge button in BillingCredits.
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const isPaymentFetching =
    stripeClientSecretAPIInProgress ||
    paymentMethodAPIInProgress ||
    clientPaymentIntentAPIInProgress ||
    paymentMethodsAPIInProgress;
  const appearance = {
    theme: mode === "dark" ? "night" : "stripe",
    // labels: mode === "dark" && "floating",

    variables: {
      colorPrimary: currentTheme?.theme?.primary,
      colorBackground: currentTheme?.theme?.inputBackgroundColor,
      colorText: currentTheme?.theme?.font_color,
      colorDanger: currentTheme?.theme?.red,
      fontFamily: "Ideal Sans, system-ui, sans-serif",
      fontSizeBase: 16,
      spacingUnit: "2px",
      borderRadius: "25px",

      // See all possible variables below
    },
  };
  const paymentPortal = useMemo(() => {
    return stripeClientSecretData?.setupIntent?.client_secret ? (
      <View
        style={{
          padding: 9,
        }}
      >
        {isPaymentMethodEditMode ? (
          <Elements
            stripe={stripePromise}
            options={{
              appearance,
              // passing the client secret obtained from the server
              clientSecret: stripeClientSecretData?.setupIntent?.client_secret,
            }}
          >
            <BillingCheckoutForm
              handlePaymentMethodSave={handlePaymentMethodSave}
              setPaymentMethodEditMode={setPaymentMethodEditMode}
              onPaymentMethodSave={onPaymentMethodSave}
            />
          </Elements>
        ) : (
          <BillingPaymentMethod
            stripeCustomerData={stripeCustomerData}
            handlePaymentPortalOpening={handlePaymentPortalOpening}
            paymentMethod={paymentMethod}
            paymentMethods={paymentMethodsRaw}
            defaultPaymentMethodId={defaultPaymentMethodId}
            setPrimaryPaymentMethod={setPrimaryPaymentMethod}
            removePaymentMethod={removePaymentMethod}
            paymentMethodAPIInProgress={paymentMethodAPIInProgress}
          />
        )}
      </View>
    ) : (
      <AppText>{t("billing.stripePortalFetchFailed")}</AppText>
    );
  }, [
    stripeClientSecretData,
    stripePromise,
    setPaymentMethodEditMode,
    isPaymentMethodEditMode,
    paymentMethod,
    paymentMethodsRaw,
    defaultPaymentMethodId,
    stripeCustomerData,
    setPrimaryPaymentMethod,
    removePaymentMethod,
    paymentMethodAPIInProgress,
    mode,
  ]);

  return (
    <View
      style={{
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        paddingHorizontal: 30,
      }}
    >
      {stripeCustomerAPIInProgress && (
        <LoadingPlaceholder
          caption={t("billing.loadingBillingInfo")}
          visible={true}
          style={{
            marginTop: 50,
          }}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
            marginTop: 50,
          }}
        />
      )}

      {!stripeCustomerAPIInProgress && stripeCustomerData && (
        <View
          style={{
            minWidth: isLargeScreen() ? 650 : 250,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              marginBottom: 0,
              marginTop: 4,
            }}
          >
            <TouchableOpacity
              onPress={onRefresh}
              disabled={stripeCustomerAPIInProgress}
              accessibilityLabel={t("common.refresh")}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={{
                padding: 4,
                flexDirection: "row",
                gap: 10,
                marginRight: 10,
              }}
            >
              <AppText
                style={{
                  color: currentTheme?.theme?.primary,
                  fontWeight: "bold",
                }}
              >
                {t("common.refresh")}
              </AppText>
              <AppIcon
                icon={AppAssets.icons.reload}
                size={18}
                color={currentTheme?.theme?.primary}
              />
            </TouchableOpacity>
          </View>
          <BillingProgressBar
            hasPaymentMethod={paymentMethod}
            hasBillingAddress={
              stripeCustomerData?.address?.line1 &&
              stripeCustomerData?.address?.city &&
              stripeCustomerData?.address?.country
            }
            hasCredits={
              ENABLE_CONSUMPTION_BILLING &&
              stripeCustomerData?.balance / -100 > 0
            }
            hasAutoRecharge={hasAutoRecharge}
            hasSpendCap={hasSpendCap}
            onAddCredits={() => setRechargeOpen(true)}
            creditsBalanceUsd={
              stripeCustomerData?.balance
                ? stripeCustomerData.balance / -100
                : 0
            }
            currentTheme={currentTheme}
          />
          {ENABLE_CONSUMPTION_BILLING && (
            <BillingCredits
              stripeCustomerData={stripeCustomerData}
              onCreditsSaved={onCreditsSaved}
              rechargeOpen={rechargeOpen}
              setRechargeOpen={setRechargeOpen}
              credits={credits}
            />
          )}
          <AppExpander
            style={{ minWidth: isLargeScreen() ? 650 : 250 }}
            isExpandedOnLoad={true}
            title={t("billing.paymentMethod")}
            icon={AppAssets.icons.billing}
          >
            {stripeClientSecretAPIInProgress ? (
              <LoadingPlaceholder
                caption={t("billing.loadingPaymentsInfo")}
                visible
              />
            ) : (
              paymentPortal
            )}
          </AppExpander>
          <AppExpander
            title={t("billing.billingDetails")}
            icon={AppAssets.icons.billingDetails}
            style={{ minWidth: isLargeScreen() ? 650 : 250 }}
            isExpandedOnLoad={true}
          >
            <BillingAddress
              stripeCustomerData={stripeCustomerData}
              saveCustomerDetails={saveCustomerDetails}
              isWizard={isWizard}
              saveInProgress={saveInProgress}
              goToPortal={goToPortal}
            />
          </AppExpander>
          <AppExpander
            title={t("billing.billingHistory")}
            icon={AppAssets.icons.invoice}
            style={{ minWidth: isLargeScreen() ? 650 : 250 }}
          >
            {stripeClientBillingHistoryAPIInProgress ? (
              <LoadingPlaceholder
                caption={t("billing.loadingBillingHistory")}
                visible
              />
            ) : (
              <BillingHistory
                billingHistory={billingHistory}
                stripeClientBillingHistoryData={stripeClientBillingHistoryData}
                loadMoreBillingHistory={loadMoreBillingHistory}
              />
            )}
          </AppExpander>
        </View>
      )}
    </View>
  );
};

const styleGenerator = (currentTheme) =>
  StyleSheet.create({
    cellWrapperStyle: {
      justifyContent: "center",
      width: "20%",
    },
    listContentContainer: {
      paddingBottom: 20,
    },
    listHeader: {
      backgroundColor: currentTheme?.theme?.lightGrey,
    },
    listItemContainer: {
      flexDirection: "row",
      padding: 10,
    },
  });

export default BillingDetailsView;
